/**
 * Portable SkinPilot AI chat transcript import/export.
 * API keys are never written. User-provided reference images can be retained,
 * while reproducible inspect images are stripped to keep files manageable.
 */

import {
  isStoredUserImagePart,
  MAX_STORED_IMAGE_DATA_BYTES,
  measureUserImageDataBytes,
  validateStoredUserImageParts,
} from "./imageAttachments";

export const CHAT_HISTORY_FORMAT = "skinpilot-ai-chat" as const;
export const CHAT_HISTORY_VERSION = 1 as const;

export type ChatHistoryConnectionMeta = {
  kind?: string;
  baseURL?: string;
  modelId?: string;
  contextWindow?: number;
  reasoningEnabled?: boolean;
  reasoningEffort?: string;
};

export type ChatHistoryDocument = {
  format: typeof CHAT_HISTORY_FORMAT;
  version: typeof CHAT_HISTORY_VERSION;
  exportedAt: string;
  connection?: ChatHistoryConnectionMeta;
  /** True when inspect PNG payloads were removed to shrink the file. */
  imagesStripped: boolean;
  messages: unknown[];
};

export type ExportChatHistoryOptions = {
  connection?: ChatHistoryConnectionMeta;
  /** @deprecated Prefer imagePolicy. true keeps every image payload. */
  keepImages?: boolean;
  /** Keep no images, only user attachments, or every image payload. */
  imagePolicy?: "none" | "user" | "all";
  now?: () => Date;
};

export type ImportChatHistoryResult = {
  document: ChatHistoryDocument;
  messages: ImportedChatMessage[];
  imagesWereStripped: boolean;
};

export type ImportedChatMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  parts: unknown[];
  metadata?: unknown;
};

const MAX_IMPORT_BYTES = 32 * 1024 * 1024;
const MAX_MESSAGES = 2_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function looksLikeBase64ImagePayload(value: unknown): boolean {
  if (typeof value !== "string") return false;
  if (value.startsWith("data:image/")) return true;
  // Raw base64 from inspectSkin tends to be long.
  return (
    value.length > 2_000 && /^[A-Za-z0-9+/=\s]+$/.test(value.slice(0, 200))
  );
}

function stripImagesFromValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripImagesFromValue);
  }
  if (!isRecord(value)) {
    if (looksLikeBase64ImagePayload(value)) {
      return "[image omitted in export]";
    }
    return value;
  }

  const next: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (
      (key === "data" ||
        key === "base64" ||
        key === "image_url" ||
        key === "url") &&
      looksLikeBase64ImagePayload(entry)
    ) {
      next[key] = "[image omitted in export]";
      continue;
    }
    if (
      key === "mediaType" &&
      typeof entry === "string" &&
      entry.startsWith("image/")
    ) {
      next[key] = entry;
      next.omitted = true;
      continue;
    }
    next[key] = stripImagesFromValue(entry);
  }
  return next;
}

function stripImagesExceptUserAttachments(value: unknown): unknown {
  if (isStoredUserImagePart(value)) return value;
  if (Array.isArray(value)) {
    return value.map(stripImagesExceptUserAttachments);
  }
  if (!isRecord(value)) {
    return looksLikeBase64ImagePayload(value)
      ? "[image omitted in export]"
      : value;
  }

  const next: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (
      (key === "data" ||
        key === "base64" ||
        key === "image_url" ||
        key === "url") &&
      looksLikeBase64ImagePayload(entry)
    ) {
      next[key] = "[image omitted in export]";
      continue;
    }
    next[key] = stripImagesExceptUserAttachments(entry);
  }
  return next;
}

export function buildChatHistoryDocument(
  messages: readonly unknown[],
  options: ExportChatHistoryOptions = {},
): ChatHistoryDocument {
  const imagePolicy =
    options.keepImages === true ? "all" : (options.imagePolicy ?? "none");
  const exportedAt = (options.now?.() ?? new Date()).toISOString();
  const sourceMessages = messages.map((message) =>
    structuredCloneSafe(message),
  );
  const sanitizedMessages = sourceMessages.map((message) => {
    if (
      imagePolicy === "all" ||
      (imagePolicy === "user" && isRecord(message) && message.role === "user")
    ) {
      return imagePolicy === "all"
        ? message
        : stripImagesExceptUserAttachments(message);
    }
    return stripImagesFromValue(message);
  });
  const imagesStripped =
    countImagePayloads(sanitizedMessages) < countImagePayloads(sourceMessages);

  return {
    format: CHAT_HISTORY_FORMAT,
    version: CHAT_HISTORY_VERSION,
    exportedAt,
    connection: options.connection
      ? {
          kind: options.connection.kind,
          baseURL: options.connection.baseURL,
          modelId: options.connection.modelId,
          contextWindow: options.connection.contextWindow,
          reasoningEnabled: options.connection.reasoningEnabled,
          reasoningEffort: options.connection.reasoningEffort,
        }
      : undefined,
    imagesStripped,
    messages: sanitizedMessages,
  };
}

function countImagePayloads(value: unknown): number {
  if (Array.isArray(value)) {
    return value.reduce((total, entry) => total + countImagePayloads(entry), 0);
  }
  if (!isRecord(value)) return looksLikeBase64ImagePayload(value) ? 1 : 0;

  let count = 0;
  for (const [key, entry] of Object.entries(value)) {
    if (
      (key === "data" ||
        key === "base64" ||
        key === "image_url" ||
        key === "url") &&
      looksLikeBase64ImagePayload(entry)
    ) {
      count += 1;
    } else {
      count += countImagePayloads(entry);
    }
  }
  return count;
}

function structuredCloneSafe<T>(value: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

export function serializeChatHistory(document: ChatHistoryDocument): string {
  return `${JSON.stringify(document, null, 2)}\n`;
}

export function chatHistoryFilename(date = new Date()): string {
  const stamp = date.toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `skinpilot-chat-${stamp}.json`;
}

export function parseChatHistoryJson(raw: string): ImportChatHistoryResult {
  if (raw.length > MAX_IMPORT_BYTES) {
    throw new Error("对话文件过大（上限约 32MB）。");
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error("对话文件不是有效的 JSON。");
  }

  if (!isRecord(parsed)) {
    throw new Error("对话文件格式不正确。");
  }

  if (parsed.format !== CHAT_HISTORY_FORMAT) {
    throw new Error(
      '不是 SkinPilot 对话导出文件（缺少 format: "skinpilot-ai-chat"）。',
    );
  }

  const version = parsed.version;
  if (version !== CHAT_HISTORY_VERSION) {
    throw new Error(`不支持的对话文件版本：${String(version)}。`);
  }

  if (!Array.isArray(parsed.messages)) {
    throw new Error("对话文件缺少 messages 数组。");
  }
  if (parsed.messages.length > MAX_MESSAGES) {
    throw new Error(`对话消息过多（最多 ${MAX_MESSAGES} 条）。`);
  }

  const messages = parsed.messages.map((message, index) =>
    normalizeImportedMessage(message, index),
  );
  if (measureUserImageDataBytes(messages) > MAX_STORED_IMAGE_DATA_BYTES) {
    throw new Error("对话中的用户图片超过 20MB 存储上限。");
  }

  const connection = isRecord(parsed.connection)
    ? {
        kind:
          typeof parsed.connection.kind === "string"
            ? parsed.connection.kind
            : undefined,
        baseURL:
          typeof parsed.connection.baseURL === "string"
            ? parsed.connection.baseURL
            : undefined,
        modelId:
          typeof parsed.connection.modelId === "string"
            ? parsed.connection.modelId
            : undefined,
        contextWindow:
          typeof parsed.connection.contextWindow === "number"
            ? parsed.connection.contextWindow
            : undefined,
        reasoningEnabled:
          typeof parsed.connection.reasoningEnabled === "boolean"
            ? parsed.connection.reasoningEnabled
            : undefined,
        reasoningEffort:
          typeof parsed.connection.reasoningEffort === "string"
            ? parsed.connection.reasoningEffort
            : undefined,
      }
    : undefined;

  return {
    document: {
      format: CHAT_HISTORY_FORMAT,
      version: CHAT_HISTORY_VERSION,
      exportedAt:
        typeof parsed.exportedAt === "string"
          ? parsed.exportedAt
          : new Date(0).toISOString(),
      connection,
      imagesStripped: parsed.imagesStripped !== false,
      messages: parsed.messages,
    },
    messages,
    imagesWereStripped: parsed.imagesStripped !== false,
  };
}

function normalizeImportedMessage(
  message: unknown,
  index: number,
): ImportedChatMessage {
  if (!isRecord(message)) {
    throw new Error(`第 ${index + 1} 条消息格式无效。`);
  }

  const role = message.role;
  if (role !== "user" && role !== "assistant" && role !== "system") {
    throw new Error(`第 ${index + 1} 条消息角色无效。`);
  }

  const id =
    typeof message.id === "string" && message.id.trim()
      ? message.id
      : `imported-${index + 1}`;

  let parts: unknown[];
  if (Array.isArray(message.parts)) {
    parts = message.parts.flatMap((part) => {
      const normalized = normalizeImportedPart(part, role);
      return normalized == null ? [] : [normalized];
    });
  } else if (typeof message.content === "string") {
    parts = message.content
      ? [{ type: "text", text: message.content, state: "done" }]
      : [];
  } else if (Array.isArray(message.content)) {
    parts = message.content.flatMap((part) => {
      const normalized = normalizeImportedPart(
        normalizeLegacyContentPart(part),
        role,
      );
      return normalized == null ? [] : [normalized];
    });
  } else {
    parts = [];
  }

  // Drop empty assistant shells that would render nothing useful.
  if (parts.length === 0 && role === "user") {
    parts = [{ type: "text", text: "", state: "done" }];
  }
  if (role === "user") {
    validateStoredUserImageParts(parts);
  }

  return {
    id,
    role,
    parts,
    metadata: message.metadata,
  };
}

function normalizeImportedPart(
  part: unknown,
  role: ImportedChatMessage["role"],
): Record<string, unknown> | null {
  if (!isRecord(part) || typeof part.type !== "string") return null;
  if (part.type === "file") {
    if (
      part.omitted === true ||
      part.url === "[image omitted in export]" ||
      part.url === "[图片已省略]"
    ) {
      const filename =
        typeof part.filename === "string"
          ? part.filename.trim().slice(0, 240)
          : "";
      return {
        type: "text",
        text: `[图片已从导出文件省略${filename ? `：${filename}` : ""}]`,
        state: "done",
      };
    }
    if (role !== "user" || !isStoredUserImagePart(part)) {
      throw new Error("对话包含无效或不受支持的图片附件。");
    }
    return {
      type: "file",
      mediaType: part.mediaType,
      filename:
        typeof part.filename === "string"
          ? part.filename.trim().slice(0, 240)
          : undefined,
      url: part.url,
    };
  }
  if (part.type !== "text" && part.type !== "reasoning") return part;
  if (typeof part.text !== "string") return null;

  return {
    ...part,
    text: part.text,
    // Imported history cannot still be actively streaming.
    state: "done",
  };
}

function normalizeLegacyContentPart(part: unknown): unknown {
  if (!isRecord(part)) return part;
  if (part.type === "text" && typeof part.text === "string") {
    return { type: "text", text: part.text, state: "done" };
  }
  return part;
}

export function downloadChatHistoryJson(
  history: ChatHistoryDocument,
  filename = chatHistoryFilename(),
): void {
  const blob = new Blob([serializeChatHistory(history)], {
    type: "application/json;charset=utf-8",
  });
  const href = URL.createObjectURL(blob);
  const anchor = globalThis.document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.rel = "noopener";
  anchor.click();
  URL.revokeObjectURL(href);
}

export async function readChatHistoryFile(
  file: File,
): Promise<ImportChatHistoryResult> {
  if (file.size > MAX_IMPORT_BYTES) {
    throw new Error("对话文件过大（上限约 32MB）。");
  }
  const text = await file.text();
  return parseChatHistoryJson(text);
}
